import { useState } from 'react';
import { Loader2, LucideIcon } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';

export type ActionButtonVariantType = 'default' | 'success' | 'danger' | 'secondary' | 'neutral' | 'custom';
export type ActionButtonSizeType = 'default' | 'mini' | 'lg';

interface ActionButtonBaseProps {
  icon?: LucideIcon;
  label?: string;
  onClick?: () => void;
  variant?: ActionButtonVariantType;
  appearance?: 'outline' | 'filled';
  disabled?: boolean;
  disabledOpacity?: number;
  loading?: boolean;
  className?: string;
  iconClassName?: string;
  size?: ActionButtonSizeType;
  borderColor?: string;
  hoverBgColor?: string;
  iconColor?: string;
  textColorOnHover?: string;
  revealLabelOnHover?: boolean;
  expanded?: boolean;
  title?: string;
  ariaLabel?: string;
  type?: 'button' | 'submit' | 'reset';
  testId?: string;
}

interface ButtonThemeType {
  borderColor: string;
  hoverBgColor: string;
  iconColor: string;
  textColorOnHover: string;
}

const VARIANT_THEMES: Record<ActionButtonVariantType, ButtonThemeType> = {
  default: {
    borderColor: 'var(--color-primary)',
    hoverBgColor: 'var(--color-primary)',
    iconColor: 'var(--color-primary)',
    textColorOnHover: 'var(--color-text-light)',
  },
  success: {
    borderColor: 'var(--color-success)',
    hoverBgColor: 'var(--color-success)',
    iconColor: 'var(--color-success)',
    textColorOnHover: 'var(--color-text-light)',
  },
  danger: {
    borderColor: 'var(--color-danger)',
    hoverBgColor: 'var(--color-danger)',
    iconColor: 'var(--color-danger)',
    textColorOnHover: 'var(--color-text-light)',
  },
  secondary: {
    borderColor: 'var(--color-accent)',
    hoverBgColor: 'var(--color-accent)',
    iconColor: 'var(--color-accent)',
    textColorOnHover: 'var(--color-text-light)',
  },
  neutral: {
    borderColor: 'var(--color-border)',
    hoverBgColor: 'var(--color-surface-elevated)',
    iconColor: 'var(--color-text-primary)',
    textColorOnHover: 'var(--color-text-primary)',
  },
  custom: {
    borderColor: 'var(--color-primary)',
    hoverBgColor: 'var(--color-primary)',
    iconColor: 'var(--color-primary)',
    textColorOnHover: 'var(--color-text-light)',
  },
};

const SIZE_STYLES: Record<ActionButtonSizeType, { compact: string; expanded: string; icon: string; hoverPadding: string; text: string }> = {
  default: {
    compact: 'w-8 h-8',
    expanded: 'h-8 px-4',
    icon: 'w-3.5 h-3.5',
    hoverPadding: 'hover:px-4',
    text: 'text-xs',
  },
  mini: {
    compact: 'w-6 h-6',
    expanded: 'h-6 px-3',
    icon: 'w-3 h-3',
    hoverPadding: 'hover:px-3',
    text: 'text-[10px]',
  },
  lg: {
    compact: 'w-10 h-10',
    expanded: 'h-10 px-5',
    icon: 'w-4 h-4',
    hoverPadding: 'hover:px-5',
    text: 'text-sm',
  },
};

export function ActionButtonBase({
  icon: Icon,
  label,
  onClick,
  variant = 'default',
  appearance = 'outline',
  disabled = false,
  disabledOpacity,
  loading = false,
  className = '',
  iconClassName = '',
  size = 'default',
  borderColor,
  hoverBgColor,
  iconColor,
  textColorOnHover,
  revealLabelOnHover = true,
  expanded = false,
  title,
  ariaLabel,
  type = 'button',
  testId,
}: ActionButtonBaseProps) {
  const isMobile = useIsMobile();
  const [isHovered, setIsHovered] = useState(false);

  const variantTheme = VARIANT_THEMES[variant] || VARIANT_THEMES.default;
  const sizes = SIZE_STYLES[size] || SIZE_STYLES.default;
  const theme: ButtonThemeType = {
    borderColor: borderColor ?? variantTheme.borderColor,
    hoverBgColor: hoverBgColor ?? variantTheme.hoverBgColor,
    iconColor: iconColor ?? variantTheme.iconColor,
    textColorOnHover: textColorOnHover ?? variantTheme.textColorOnHover,
  };

  const hasIcon = Boolean(Icon);
  const hasLabel = Boolean(label);
  const isLoadingWithLabel = loading && hasLabel;
  // UX backlog M.7 : sur mobile, on garde le label visible MÊME quand le
  // bouton est disabled — sinon l'utilisateur voit une icône isolée et ne
  // comprend pas l'action (cf footer "Créer un post" où Save/Calendar étaient
  // icon-only quand le contenu est vide). Sur desktop, on garde le pattern
  // icon-only + hover-reveal (sauf disabled qui reste compact pour ne pas
  // perturber l'œil).
  const alwaysExpandedForLabel =
    hasLabel && (!revealLabelOnHover || isMobile || !hasIcon) && (isMobile || !disabled);
  const hoverExpandable = !isMobile && revealLabelOnHover && hasLabel && hasIcon && !expanded && !disabled && !loading;
  const isExpandedState = (expanded || alwaysExpandedForLabel || isLoadingWithLabel) && !(disabled && !loading && !isMobile);
  // UX backlog 2.2 : disabled visuellement reconnaissable.
  // `cursor-not-allowed` + opacity baissée pour différencier des boutons actifs.
  // Note : `disabled={true}` côté navigateur applique déjà un grisé, mais on
  // force ici une opacity explicite pour cohérence cross-browser.
  const disabledClasses = disabled
    ? (loading ? 'cursor-wait opacity-100' : 'cursor-not-allowed opacity-50')
    : loading
      ? 'cursor-wait'
      : 'cursor-pointer';
  const isFilled = appearance === 'filled';
  const effectiveBgColor = isFilled ? theme.hoverBgColor : (isHovered ? theme.hoverBgColor : 'transparent');
  const effectiveForegroundColor = isFilled
    ? theme.textColorOnHover
    : (isHovered ? theme.textColorOnHover : theme.iconColor);

  const buttonSizeClasses = isExpandedState ? sizes.expanded : sizes.compact;
  const hoverClasses = hoverExpandable ? `hover:w-auto ${sizes.hoverPadding}` : '';
  const showStaticLabel = hasLabel && isExpandedState;
  const showHoverLabel = !showStaticLabel && hasLabel && isHovered;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      title={title}
      // V4-A11Y-ICON-BUTTON-LABELS : les boutons icon-only (pas de label texte
      // rendu) doivent quand même avoir un nom accessible (WCAG 4.1.2). On
      // retombe sur le label texte, puis sur le title, à défaut d'ariaLabel.
      aria-label={ariaLabel ?? label ?? title}
      data-testid={testId}
      className={`group ${buttonSizeClasses} rounded-[100px] border transition-all duration-300 flex items-center justify-center flex-shrink-0 overflow-hidden disabled:opacity-50 ${hoverClasses} ${disabledClasses} ${className}`}
      style={{
        borderColor: theme.borderColor,
        backgroundColor: effectiveBgColor,
        opacity: disabled ? (disabledOpacity ?? undefined) : undefined,
      }}
      onMouseEnter={isMobile || disabled ? undefined : () => setIsHovered(true)}
      onMouseLeave={isMobile || disabled ? undefined : () => setIsHovered(false)}
    >
      {loading ? (
        <Loader2 className={`${sizes.icon} transition-colors flex-shrink-0 animate-spin ${iconClassName}`} style={{ color: isHovered ? theme.textColorOnHover : theme.iconColor }} />
      ) : (
        Icon && <Icon className={`${sizes.icon} transition-colors flex-shrink-0 ${iconClassName}`} style={{ color: effectiveForegroundColor }} />
      )}

      {showStaticLabel && hasLabel && (
        <span className={`${sizes.text} font-medium whitespace-nowrap ${hasIcon ? 'ml-2' : ''}`} style={{ color: disabled ? 'var(--color-text-secondary)' : effectiveForegroundColor }}>
          {label}
        </span>
      )}

      {showHoverLabel && (
        <span
          className={`${sizes.text} font-medium whitespace-nowrap transition-all duration-300 overflow-hidden opacity-100 ml-2 max-w-[220px]`}
          style={{ color: disabled ? 'var(--color-text-secondary)' : effectiveForegroundColor }}
        >
          {label}
        </span>
      )}
    </button>
  );
}
